/* global URL */

const DEFAULT_REDACTED_VALUE = '<redacted>';
const MAX_SERIALIZED_JSON_DEPTH = 20;

const EXACT_SENSITIVE_URL_KEYS = new Set([
  'authorization',
  'auth',
  'password',
  'passwd',
  'pwd',
  'key',
  'sig',
  'signature',
  'credential',
  'accesskey',
  'accesskeyid',
  'accesskeysecret',
  'secretkey',
  'securitytoken',
  'xamzcredential',
  'xamzsignature',
  'xamzsecuritytoken',
]);

const EXACT_SENSITIVE_DIAGNOSTIC_KEYS = new Set([
  'authorization',
  'auth',
  'cookie',
  'credential',
  'credentials',
  'hmac',
  'password',
  'passwd',
  'proxyauthorization',
  'pwd',
  'secret',
  'session',
  'sig',
  'signature',
  'token',
]);

function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function redactKnownSecretPathSegments(parsedUrl: URL, redactedValue: string): void {
  const segments = parsedUrl.pathname.split('/');
  const hooksIndex = segments.findIndex((segment, index) => {
    const hookKind = (segments[index + 1] || '').toLowerCase();
    return segment.toLowerCase() === 'hooks'
      && (hookKind === 'flow' || hookKind === 'form');
  });
  if (hooksIndex < 0) {
    return;
  }

  const secretIndex = hooksIndex + 3;
  if (segments[secretIndex]) {
    segments[secretIndex] = redactedValue;
    parsedUrl.pathname = segments.join('/');
  }
}

export function isSensitiveUrlKey(key: string): boolean {
  const normalizedKey = normalizeKey(key);
  if (!normalizedKey) {
    return false;
  }
  return EXACT_SENSITIVE_URL_KEYS.has(normalizedKey)
    || normalizedKey.includes('token')
    || normalizedKey.includes('secret')
    || normalizedKey.includes('password')
    || normalizedKey.includes('apikey')
    || normalizedKey.endsWith('credential')
    || normalizedKey.endsWith('signature');
}

export function isSensitiveDiagnosticKey(key: string): boolean {
  const normalizedKey = normalizeKey(key);
  if (!normalizedKey) {
    return false;
  }
  return EXACT_SENSITIVE_DIAGNOSTIC_KEYS.has(normalizedKey)
    || normalizedKey.endsWith('accesskey')
    || normalizedKey.endsWith('apikey')
    || normalizedKey.endsWith('authkey')
    || normalizedKey.endsWith('clientsecret')
    || normalizedKey.endsWith('cookie')
    || normalizedKey.endsWith('credential')
    || normalizedKey.endsWith('password')
    || normalizedKey.endsWith('privatekey')
    || normalizedKey.endsWith('sessionid')
    || normalizedKey.endsWith('signature')
    || normalizedKey.endsWith('token');
}

export function isSensitiveRequestHeader(key: string): boolean {
  const normalizedKey = normalizeKey(String(key || ''));
  return normalizedKey === 'authorization'
    || normalizedKey === 'proxyauthorization'
    || normalizedKey === 'xlinkauthorization'
    || normalizedKey === 'stwauthorization';
}

function isSensitiveHeaderKey(key: string): boolean {
  const normalizedKey = normalizeKey(key);
  return (key.includes('-') && isSensitiveDiagnosticKey(key))
    || isSensitiveRequestHeader(key)
    || normalizedKey === 'authorization'
    || normalizedKey === 'auth'
    || normalizedKey === 'cookie'
    || normalizedKey === 'hmac'
    || normalizedKey === 'proxyauthorization'
    || normalizedKey === 'session'
    || normalizedKey === 'xlinkauthorization'
    || normalizedKey === 'stwauthorization'
    || normalizedKey.endsWith('apikey')
    || normalizedKey.endsWith('authkey')
    || normalizedKey.endsWith('cookie')
    || normalizedKey.endsWith('signature')
    || normalizedKey.endsWith('token');
}

export function redactSensitiveUrl(
  rawUrl: string,
  redactedValue: string = DEFAULT_REDACTED_VALUE,
): string {
  const redactParsedUrl = (parsedUrl: URL): URL => {
    if (parsedUrl.username) {
      parsedUrl.username = redactedValue;
    }
    if (parsedUrl.password) {
      parsedUrl.password = redactedValue;
    }
    redactKnownSecretPathSegments(parsedUrl, redactedValue);
    for (const key of Array.from(parsedUrl.searchParams.keys())) {
      if (isSensitiveUrlKey(key)) {
        parsedUrl.searchParams.set(key, redactedValue);
      }
    }
    return parsedUrl;
  };

  try {
    return redactParsedUrl(new URL(rawUrl)).toString();
  } catch (_error) {
    if (rawUrl.startsWith('/')) {
      try {
        const parsedUrl = redactParsedUrl(new URL(rawUrl, 'https://redaction.local'));
        return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
      } catch (_relativeError) {
        return rawUrl;
      }
    }
    return rawUrl;
  }
}

export function redactSensitiveUrls(
  urls: string[],
  redactedValue: string = DEFAULT_REDACTED_VALUE,
): string[] {
  return urls.map(url => redactSensitiveUrl(url, redactedValue));
}

function redactUrlTextMatch(match: string, redactedValue: string): string {
  const trailingPunctuation = match.match(/[),.;!?]+$/)?.[0] || '';
  const url = trailingPunctuation ? match.slice(0, -trailingPunctuation.length) : match;
  return `${redactSensitiveUrl(url, redactedValue)}${trailingPunctuation}`;
}

export function redactSensitiveUrlsInText(
  text: string,
  redactedValue: string = DEFAULT_REDACTED_VALUE,
): string {
  return text.replace(
    /https?:\/\/[^\s"'<>]+/gi,
    match => redactUrlTextMatch(match, redactedValue),
  );
}

function redactSensitiveHeaderLines(text: string, redactedValue: string): string {
  return text.replace(
    /^([ \t]*)([A-Za-z][A-Za-z0-9_-]*)([ \t]*:[ \t]*)([^\r\n]*)/gm,
    (match, indentation: string, key: string, separator: string) => (
      isSensitiveHeaderKey(key)
        ? `${indentation}${key}${separator}${redactedValue}`
        : match
    ),
  );
}

function isAlreadyRedacted(value: string, redactedValue: string): boolean {
  const withoutPunctuation = value.replace(/[),.;!?]+$/, '');
  return withoutPunctuation.toLowerCase() === redactedValue.toLowerCase()
    || withoutPunctuation.toLowerCase() === encodeURIComponent(redactedValue).toLowerCase();
}

function redactSensitiveAssignments(text: string, redactedValue: string): string {
  const sensitiveAssignment = /(["']?)(authorization|proxy[-_]?authorization|password|passwd|pwd|token|access[-_]?token|refresh[-_]?token|auth[-_]?token|api[-_]?key|access[-_]?key|client[-_]?secret|private[-_]?key|secret|credential|signature|cookie|set[-_]?cookie|session)\1(\s*(?:=|:|\bis\b)\s*)("[^"\r\n]*"|'[^'\r\n]*'|[^\s,;&}\]\r\n]+)/gi;
  return text.replace(sensitiveAssignment, (...args: string[]) => {
    const [match, quote, key, separator, value] = args;
    const valueQuote = value[0] === '"' || value[0] === "'" ? value[0] : '';
    const unquotedValue = valueQuote ? value.slice(1, -1) : value;
    if (isAlreadyRedacted(unquotedValue, redactedValue)) {
      return match;
    }
    const replacement = valueQuote
      ? `${valueQuote}${redactedValue}${valueQuote}`
      : redactedValue;
    return `${quote}${key}${quote}${separator}${replacement}`;
  });
}

function redactParsedJsonValue(value: unknown, depth: number, redactedValue: string): unknown {
  if (Array.isArray(value)) {
    return value.map(entry => redactParsedJsonValue(entry, depth, redactedValue));
  }
  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, entryValue] of Object.entries(value)) {
      result[key] = isSensitiveDiagnosticKey(key) && entryValue !== null
        ? redactedValue
        : redactParsedJsonValue(entryValue, depth, redactedValue);
    }
    return result;
  }
  if (typeof value === 'string') {
    return redactSensitiveDiagnosticTextInternal(value, depth, redactedValue);
  }
  return value;
}

function redactSerializedJson(text: string, depth: number, redactedValue: string): string | null {
  if (depth >= MAX_SERIALIZED_JSON_DEPTH) {
    return null;
  }
  const trimmed = text.trim();
  if (!(trimmed.startsWith('{') && trimmed.endsWith('}'))
    && !(trimmed.startsWith('[') && trimmed.endsWith(']'))) {
    return null;
  }
  try {
    const parsed = JSON.parse(trimmed);
    const redacted = JSON.stringify(redactParsedJsonValue(parsed, depth + 1, redactedValue));
    const start = text.indexOf(trimmed);
    return `${text.slice(0, start)}${redacted}${text.slice(start + trimmed.length)}`;
  } catch (_error) {
    return null;
  }
}

function redactSensitiveDiagnosticTextInternal(
  text: string,
  depth: number,
  redactedValue: string,
): string {
  const serializedJson = redactSerializedJson(text, depth, redactedValue);
  if (serializedJson !== null) {
    return serializedJson;
  }

  let redacted = redactSensitiveUrlsInText(text, redactedValue);
  redacted = redacted.replace(
    /\/(?:api\/v1\/)?hooks\/(?:flow|form)\/[^\s"'<>]+/gi,
    match => redactUrlTextMatch(match, redactedValue),
  );
  redacted = redactSensitiveHeaderLines(redacted, redactedValue);
  redacted = redacted.replace(
    /(\b(?:proxy-)?authorization\s*[:=]\s*)(?:Basic|Digest|HMAC)\s+([^\s,;}\]"']+)/gi,
    (match, prefix: string, value: string) => (
      isAlreadyRedacted(value, redactedValue) ? match : `${prefix}${redactedValue}`
    ),
  );
  redacted = redacted.replace(
    /\b(Bearer[ \t]+)([^\s,;}\]"']+)/gi,
    (match, prefix: string, value: string) => (
      isAlreadyRedacted(value, redactedValue) ? match : `${prefix}${redactedValue}`
    ),
  );
  return redactSensitiveAssignments(redacted, redactedValue);
}

export function redactSensitiveDiagnosticText(
  text: string,
  redactedValue: string = DEFAULT_REDACTED_VALUE,
): string {
  return redactSensitiveDiagnosticTextInternal(text, 0, redactedValue);
}
